import { flow, makeObservable, observable, runInAction, toJS } from 'mobx';
import {
  BusinessLineSalesItemOptionModel,
  IProactiveRxRuleForm,
  ProactivePrescriptionRuleItemModel,
  ProactivePrescriptionRuleModel,
  ProactivePrescriptionVeterinarianRuleItemModel,
  ProactivePrescriptionVeterinarianRuleModel,
} from '@common/models';
import { ProactivePrescriptionService } from '../../common/services/systemTables/proactivePrescriptionService';
import { BusinessLineEnum } from '../models/Enums/BusinessLineEnum';

export class ProactivePrescriptionRulesStore {
  @observable proactivePrescriptionRules: ProactivePrescriptionRuleModel[] = [];
  @observable selectedProactivePrescriptionRule:
    | ProactivePrescriptionRuleModel
    | undefined;
  @observable
  salesItemOptionsByProactivePrescriptionRule: BusinessLineSalesItemOptionModel[] =
    [];

  constructor() {
    makeObservable(this, undefined, { autoBind: true });
  }
  saveProactivePrescriptionRule = flow(function* (
    this: ProactivePrescriptionRulesStore,
    proactivePrescriptionRule: ProactivePrescriptionRuleModel,
  ) {
    this.selectedProactivePrescriptionRule =
      yield ProactivePrescriptionService.updateProactivePrescriptionRule(
        proactivePrescriptionRule,
      );
  });

  deleteProactivePrescriptionRule = flow(function* (
    this: ProactivePrescriptionRulesStore,
    proactivePrescriptionRuleId,
  ) {
    yield ProactivePrescriptionService.deleteProactivePrescriptionRule(
      proactivePrescriptionRuleId,
    );
  });

  getProactivePrescriptionRules = flow(function* (
    this: ProactivePrescriptionRulesStore,
  ) {
    this.proactivePrescriptionRules =
      yield ProactivePrescriptionService.getProactivePrescriptionRules();
  });

  getProactivePrescriptionRule = flow(function* (
    this: ProactivePrescriptionRulesStore,
    proactivePrescriptionRuleId: number,
  ) {
    this.selectedProactivePrescriptionRule =
      yield ProactivePrescriptionService.getProactivePrescriptionRule(
        proactivePrescriptionRuleId,
      );

    const salesItemOptions: BusinessLineSalesItemOptionModel[] =
      yield ProactivePrescriptionService.getSalesItemOptionsByProactivePrescriptionRule(
        this.selectedProactivePrescriptionRule?.proactivePrescriptionRuleId!,
      );

    runInAction(() => {
      this.salesItemOptionsByProactivePrescriptionRule = salesItemOptions;
    });
  });

  updateProactiveRxRule(
    this: ProactivePrescriptionRulesStore,
    data: IProactiveRxRuleForm,
  ): void {
    const filtered = this.getFilteredElements(data);
    const existingItems =
      this.selectedProactivePrescriptionRule!.proactivePrescriptionRuleItems;

    const existingItemsMap = new Map<
      number,
      ProactivePrescriptionRuleItemModel
    >(existingItems.map((item) => [item.salesItemOptionId, item]));

    const updatedValue: ProactivePrescriptionRuleModel = {
      ...toJS(this.selectedProactivePrescriptionRule!),
      proactivePrescriptionRuleItems: [
        ...filtered.map((item) => {
          const existingItem = existingItemsMap.get(item.salesItemOptionId!);
          return new ProactivePrescriptionRuleItemModel({
            proactivePrescriptionRuleId:
              this.selectedProactivePrescriptionRule!
                .proactivePrescriptionRuleId,
            salesItemOptionId: item.salesItemOptionId,
            businessLineId: item.businessLineId,
            proactivePrescriptionRuleItemId:
              existingItem?.proactivePrescriptionRuleItemId ?? 0,
            rowVersion: '',
          });
        }),
      ],
    };
    runInAction(() => {
      this.selectedProactivePrescriptionRule = updatedValue;
    });
  }

  addProactiveRxVetRule = flow(function* (
    this: ProactivePrescriptionRulesStore,
    proactiveRxVetRule: ProactivePrescriptionVeterinarianRuleModel,
  ) {
    yield ProactivePrescriptionService.addProactiveRxVetRule(
      proactiveRxVetRule,
    );
  });

  updateProactiveRxVetRule = flow(function* (
    this: ProactivePrescriptionRulesStore,
    proactiveRxVetRule: ProactivePrescriptionVeterinarianRuleModel,
  ) {
    yield ProactivePrescriptionService.updateProactiveRxVetRule(
      proactiveRxVetRule,
    );
  });

  updateProactiveRxVetRuleObject(
    this: ProactivePrescriptionRulesStore,
    data: IProactiveRxRuleForm,
    originalVetRule: ProactivePrescriptionVeterinarianRuleModel,
  ): ProactivePrescriptionVeterinarianRuleModel {
    const filtered = this.getFilteredElements(data);
    const existingItems =
      originalVetRule.proactivePrescriptionVeterinarianRuleItems ?? [];

    const existingItemsMap = new Map<
      number,
      ProactivePrescriptionVeterinarianRuleItemModel
    >(existingItems.map((item) => [item.salesItemOptionId, item]));

    const updatedValue: ProactivePrescriptionVeterinarianRuleModel = {
      ...toJS(originalVetRule),
      proactivePrescriptionVeterinarianRuleItems: [
        ...filtered.map((item) => {
          const existingItem = existingItemsMap.get(item.salesItemOptionId!);
          return new ProactivePrescriptionVeterinarianRuleItemModel({
            proactivePrescriptionVeterinarianRuleId:
              originalVetRule.proactivePrescriptionVeterinarianRuleId,
            salesItemOptionId: item.salesItemOptionId,
            businessLineId: item.businessLineId,
            proactivePrescriptionVeterinarianRuleItemId:
              existingItem?.proactivePrescriptionVeterinarianRuleItemId ?? 0,
            rowVersion: '',
          });
        }),
      ],
    };
    return updatedValue;
  }

  private getFilteredElements(data: IProactiveRxRuleForm) {
    return [
      ...data.hospitalList.map((item) => ({
        ...item,
        businessLineId: BusinessLineEnum.Hospital,
      })),
      ...data.clinicList.map((item) => ({
        ...item,
        businessLineId: BusinessLineEnum.Clinic,
      })),
    ].filter((item) => item.isSelected);
  }
}
